// src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { updatePassword, editUserResetPasswordFlag, getUserInfo, editUserStatus } from '../services/userservice';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [userStatus, setUserStatus] = useState(null); // 0=INACTIVO, 1=ACTIVO, 2=BLOQUEADO
  const [resetFlagEnabled, setResetFlagEnabled] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      // Obtener username del token (formato: username_timestamp_random)
      const tokenParam = searchParams.get('token');
      if (tokenParam) {
        
        // Extraer username del token
        // El token tiene formato: username_timestamp_random
        // Como el username puede contener guiones bajos (ej: RMarquez_06101),
        // extraemos las últimas 2 partes (timestamp y random) y el resto es el username
        const parts = tokenParam.split('_');
        const usernameFromToken = parts.slice(0, -2).join('_');
        
        try {
          setUsername(usernameFromToken);
          
          // Obtener información completa del usuario
          const userInfo = await getUserInfo(usernameFromToken);
          
          // ✅ VALIDACIÓN DE SEGURIDAD: Verificar si el resetflag está habilitado
          const passwordResetFlag = userInfo.passwordResetFlag ?? userInfo.resetFlagPassword ?? 0;
          
          if (passwordResetFlag === 0) {
            // El link ya fue usado o nunca fue habilitado
            console.warn('⚠️ Link de reseteo inválido - resetflag no está habilitado');
            setLoadingUserData(false);
            
            Swal.fire({
              icon: 'error',
              title: 'Link no válido o expirado',
              html: `
                <div style="text-align: center; padding: 15px;">
                  <p style="margin-bottom: 15px; font-size: 16px; font-weight: 500;">
                    Este link ya fue utilizado o no es valido.
                  </p>
                  <hr style="margin: 15px 0; border: none; border-top: 1px solid #ddd;">
                  <p style="font-size: 14px; color: #666;">
                    Si necesita cambiar su contraseña nuevamente, contacte al administrador del sistema.
                  </p>
                </div>
              `,
              confirmButtonText: 'Ir al Login',
              confirmButtonColor: '#CC0000',
              width: '500px',
              allowOutsideClick: false
            }).then(() => {
              navigate('/login');
            });
            return;
          }
          
          // El resetflag está habilitado (=1), permitir el cambio
          setResetFlagEnabled(true);
          
          // Construir nombre completo
          const firstName = userInfo.name || usernameFromToken;
          const lastName = userInfo.lastName || userInfo.lastname || '';
          const completeName = lastName ? `${firstName} ${lastName}` : firstName;
          
          setFullName(completeName);
          
          // Guardar el estado actual del usuario (para saber si estaba bloqueado)
          setUserStatus(userInfo.status);
        } catch (error) {
          console.error('Error al cargar usuario:', error);
          setLoadingUserData(false);
          
          Swal.fire({
            icon: 'error',
            title: 'Error al cargar información',
            text: 'No se pudo verificar la información del usuario. Por favor, intente nuevamente.',
            confirmButtonColor: '#CC0000'
          }).then(() => {
            navigate('/login');
          });
          return;
        }
      } else {
        // Si no hay token, redirigir al login
        Swal.fire({
          icon: 'error',
          title: 'Link inválido',
          text: 'El link de reseteo no es válido',
          confirmButtonColor: '#CC0000'
        }).then(() => {
          navigate('/login');
        });
      }
      setLoadingUserData(false);
    };
    
    loadUserData();
  }, [searchParams, navigate]);

  const validatePassword = (password, username) => {
    if (password.length < 8) {
      return {
        isValid: false,
        message: 'La contraseña debe tener al menos 8 caracteres'
      };
    }

    // Validar que no contenga caracteres peligrosos de SQL: ' * -
    if (/['*-]/.test(password)) {
      return {
        isValid: false,
        message: 'La contraseña no puede contener los caracteres: \' * -'
      };
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&()_+=\[\]{}|;:",.><?\\/\\`~]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      return {
        isValid: false,
        message: 'La contraseña debe contener mayúsculas, minúsculas, números y caracteres especiales (!@#$%^&)'
      };
    }

    // Validar que la contraseña no contenga el nombre de usuario (case-insensitive)
    if (username && password.toLowerCase().includes(username.toLowerCase())) {
      return {
        isValid: false,
        message: 'La contraseña no puede contener el nombre de usuario'
      };
    }

    return { isValid: true, message: '' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor, complete todos los campos',
        confirmButtonColor: '#CC0000'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Las contraseñas no coinciden',
        text: 'Por favor, verifique que ambas contraseñas sean iguales',
        confirmButtonColor: '#CC0000'
      });
      return;
    }

    // Validar complejidad de contraseña
    const validation = validatePassword(newPassword, username);
    if (!validation.isValid) {
      Swal.fire({
        icon: 'warning',
        title: 'Contraseña inválida',
        text: validation.message,
        confirmButtonColor: '#CC0000'
      });
      return;
    }

    setLoading(true);

    try {
      // ✅ VALIDACIÓN DE SEGURIDAD: Verificar resetflag nuevamente antes de proceder
      const userInfo = await getUserInfo(username);
      const currentResetFlag = userInfo.passwordResetFlag ?? userInfo.resetFlagPassword ?? 0;
      
      if (currentResetFlag === 0) {
        // El resetflag fue deshabilitado - No permitir el cambio
        setLoading(false);
        Swal.fire({
          icon: 'error',
          title: 'Link ya utilizado',
          html: `
            <div style="text-align: center; padding: 15px;">
              <p style="margin-bottom: 15px; font-size: 16px; font-weight: 500;">
                Este link de cambio de contraseña ya fue utilizado.
              </p>
              <hr style="margin: 15px 0; border: none; border-top: 1px solid #ddd;">
              <p style="font-size: 14px; color: #666;">
                Si necesita cambiar su contraseña nuevamente, contacte al administrador del sistema.
              </p>
            </div>
          `,
          confirmButtonText: 'Ir al Login',
          confirmButtonColor: '#CC0000',
          width: '500px',
          allowOutsideClick: false
        }).then(() => {
          navigate('/login');
        });
        return;
      }
      
      // Paso 1: Actualizar la contraseña
      await updatePassword(username, newPassword);

      // Paso 2: Desactivar el flag de reseteo obligatorio (permitir login normal)
      await editUserResetPasswordFlag(username, 0);

      // Paso 3: Desbloquear al usuario si estaba bloqueado (cambiar status a ACTIVO = 1)
      await editUserStatus(username, 1);

      // Mostrar mensaje apropiado según el estado original del usuario
      const wasBlocked = userStatus === 2; // 2 = BLOQUEADO
      
      Swal.fire({
        icon: 'success',
        title: wasBlocked ? '¡Contraseña cambiada y cuenta activada!' : '¡Contraseña actualizada!',
        text: wasBlocked 
          ? 'Su contraseña ha sido actualizada exitosamente y su cuenta ha sido desbloqueada. Ahora puede iniciar sesión con su nueva contraseña.'
          : 'Su contraseña ha sido actualizada exitosamente. Ahora puede iniciar sesión con su nueva contraseña.',
        confirmButtonColor: '#CC0000'
      }).then(() => {
        navigate('/login');
      });
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo cambiar la contraseña. Por favor, intente nuevamente.',
        confirmButtonColor: '#CC0000'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
      <div className="fixed top-0 left-0 right-0 h-2 gradient-red shadow-glow-red"></div>
      
      <div className="relative w-full max-w-md animate-fade-in-up">
        <div className="glass border-2 border-gray-200 rounded-3xl shadow-2xl overflow-hidden hover:shadow-glow-red transition-all duration-500">
          
          <div className="p-6">
            <div className="flex flex-col items-center mb-6">
              <div className="relative mb-3">
                <div className="absolute inset-0 bg-honda-100 rounded-2xl blur-2xl opacity-50"></div>
                <img 
                  src="/Hondalogo.png" 
                  alt="Honda Logo" 
                  className="relative h-16 w-auto object-contain drop-shadow-xl"
                />
              </div>
              <p className="text-gray-600 text-sm font-medium">Restablecer Contraseña</p>
              <div className="flex items-center gap-2 mt-2">
                <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-honda-400 rounded-full"></div>
                <div className="w-12 h-1 gradient-red rounded-full shadow-glow-red"></div>
                <div className="w-8 h-0.5 bg-gradient-to-l from-transparent to-honda-400 rounded-full"></div>
              </div>
            
              <p className="text-xs text-gray-500 text-center border-t border-gray-100 pt-2 mt-2">
                Complete los campos para crear su nueva contraseña
              </p>
            </div>
            
            {/* Información del usuario */}
            {loadingUserData ? (
              <div className="mb-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200 flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-honda-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-gray-600">Cargando información...</span>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-gradient-to-br from-honda-50 to-red-50 rounded-xl border-2 border-honda-200 shadow-inner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-honda-500 to-honda-700 rounded-full flex items-center justify-center shadow-md">
                    <span className="material-icons text-white text-xl">person</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide">Usuario</p>
                    <p className="text-sm font-bold text-gray-800">{fullName}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2 uppercase tracking-wide flex items-center gap-2">
                  <span className="material-icons text-honda-600 text-lg">lock</span>
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-honda-500 focus:ring-4 focus:ring-honda-100 transition-all duration-300 shadow-inner-soft"
                    placeholder="Ingrese su nueva contraseña"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-honda-600 transition-colors"
                  >
                    <span className="material-icons text-xl">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                <p className="text-gray-400 text-xs mt-2 flex items-center gap-1">
                  <span className="material-icons text-xs">info</span>
                  Mínimo 8 caracteres
                </p>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2 uppercase tracking-wide flex items-center gap-2">
                  <span className="material-icons text-honda-600 text-lg">check_circle</span>
                  Confirmar Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-honda-500 focus:ring-4 focus:ring-honda-100 transition-all duration-300 shadow-inner-soft"
                    placeholder="Confirme su nueva contraseña"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-honda-600 transition-colors"
                  >
                    <span className="material-icons text-xl">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 gradient-red hover:shadow-glow-red-lg text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft uppercase tracking-wide ripple-effect hover:-translate-y-0.5"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Guardando...</span>
                  </div>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span>Guardar Nueva Contraseña</span>
                  </span>
                )}
              </button>
            </form>
            
          </div>
        </div>
        
        <div className="mt-4 flex flex-col items-center justify-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-gray-400 text-xs font-medium">powered by</span>
            <img 
              src="/logoDatco.png" 
              alt="Datco Logo" 
              className="h-6 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
            />
          </div>
          <p className="text-center text-gray-400 text-xs flex items-center gap-2">
            <span className="material-icons text-xs">copyright</span>
            2026 Honda Motor Co., Ltd. - Todos los derechos reservados
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
